import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { X, Send, Sparkles, Loader2, Maximize2, Mic, MicOff } from 'lucide-react';
import { useVoiceInput } from '@/hooks/useVoiceInput';
import axios from 'axios';

const API = '/api';

export const CoachPanel = ({ isOpen, onClose, initialMessage = '' }) => {
  const navigate = useNavigate();
  const { authHeaders, user } = useAuth();
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [sending, setSending] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);
  const initialSent = useRef(false);
  const { listening, toggleListening, supported: voiceSupported } = useVoiceInput((text) => setInput(text));

  useEffect(() => {
    if (isOpen) {
      setTimeout(() => inputRef.current?.focus(), 300);
      // Auto-send initial message if provided and not already sent
      if (initialMessage && !initialSent.current && messages.length === 0) {
        initialSent.current = true;
        setInput(initialMessage);
        setTimeout(() => {
          sendMessageDirect(initialMessage);
        }, 400);
      }
    }
    if (!isOpen) {
      initialSent.current = false;
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const sendMessageDirect = async (msg) => {
    if (!msg.trim() || sending) return;
    setSending(true);
    setInput('');

    const tempMsg = { role: 'user', content: msg, message_id: 'temp_' + Date.now() };
    setMessages(prev => [...prev, tempMsg]);

    try {
      const res = await axios.post(`${API}/ai-coach/chat`, {
        message: msg,
        session_id: sessionId,
      }, authHeaders());

      const { session_id, user_message, ai_message } = res.data;
      if (!sessionId) setSessionId(session_id);

      setMessages(prev => [
        ...prev.filter(m => m.message_id !== tempMsg.message_id),
        user_message,
        ai_message,
      ]);
    } catch (err) {
      setMessages(prev => prev.filter(m => m.message_id !== tempMsg.message_id));
    }
    setSending(false);
  };

  const sendMessage = async () => {
    if (!input.trim() || sending) return;
    await sendMessageDirect(input.trim());
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex justify-end" data-testid="coach-panel-overlay">
      <div className="absolute inset-0 bg-black/30" onClick={onClose} />
      <div className="relative w-full max-w-md h-full bg-[#F5F2ED] dark:bg-[#0F1419] shadow-2xl flex flex-col animate-in slide-in-from-right duration-200">
        {/* Header */}
        <div className="flex items-center justify-between px-4 py-3 border-b border-[#1A3A4A]/10 dark:border-white/10 bg-white/80 dark:bg-[#141C22]/90">
          <div className="flex items-center gap-2">
            <Sparkles className="w-4 h-4 text-[#6B9E8A]" />
            <span className="font-semibold text-sm text-[#1A3A4A] dark:text-[#F0EDE8]">AI Coach</span>
          </div>
          <div className="flex items-center gap-1">
            <button
              data-testid="coach-panel-expand"
              onClick={() => { onClose(); navigate('/ai-coach'); }}
              className="p-2 rounded-lg hover:bg-[#1A3A4A]/5 dark:hover:bg-white/10 transition"
              title="Open full page"
            >
              <Maximize2 className="w-4 h-4 text-[#7A7A72]" />
            </button>
            <button
              data-testid="coach-panel-close"
              onClick={onClose}
              className="p-2 rounded-lg hover:bg-[#1A3A4A]/5 dark:hover:bg-white/10 transition"
            >
              <X className="w-4 h-4 text-[#7A7A72]" />
            </button>
          </div>
        </div>

        {/* Messages */}
        <div className="flex-1 overflow-y-auto px-4 py-4">
          {messages.length === 0 && (
            <div className="flex flex-col items-center justify-center h-full text-center" data-testid="coach-panel-empty">
              <div className="w-12 h-12 rounded-xl bg-[#6B9E8A]/10 flex items-center justify-center mb-3">
                <Sparkles className="w-6 h-6 text-[#6B9E8A]" />
              </div>
              <p className="text-sm text-[#7A7A72] dark:text-[#A1A1AA] mb-4">
                Ask me anything about nutrition!
              </p>
              <div className="flex flex-wrap gap-2 justify-center px-2">
                {["Build my next meal", "Review my meals today", "How can I get more protein?"].map((prompt, i) => (
                  <button
                    key={i}
                    data-testid={`panel-quick-${i}`}
                    onClick={() => { setInput(prompt); setTimeout(() => inputRef.current?.focus(), 50); }}
                    className="text-xs px-3 py-1.5 rounded-full border border-[#6B9E8A]/20 text-[#6B9E8A] hover:bg-[#6B9E8A]/5 transition"
                  >
                    {prompt}
                  </button>
                ))}
              </div>
            </div>
          )}
          <div className="space-y-3">
            {messages.map((msg, i) => (
              <div key={msg.message_id || i} className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                <div className={`max-w-[85%] rounded-2xl px-3.5 py-2.5 text-sm ${
                  msg.role === 'user'
                    ? 'bg-[#6B9E8A] text-white rounded-br-md'
                    : 'rounded-[16px] bg-white/[0.52] dark:bg-white/[0.06] text-[#1A3A4A] dark:text-[#F0EDE8] border border-[#1A3A4A]/5 dark:border-white/10 rounded-bl-md'
                }`}>
                  <p className="whitespace-pre-wrap">{msg.content}</p>
                </div>
              </div>
            ))}
            {sending && (
              <div className="flex justify-start">
                <div className="rounded-[16px] bg-white/[0.52] dark:bg-white/[0.06] border border-[#1A3A4A]/5 dark:border-white/10 rounded-2xl rounded-bl-md px-3.5 py-2.5">
                  <div className="flex gap-1">
                    <div className="w-1.5 h-1.5 rounded-full bg-[#6B9E8A] animate-bounce" style={{ animationDelay: '0ms' }} />
                    <div className="w-1.5 h-1.5 rounded-full bg-[#6B9E8A] animate-bounce" style={{ animationDelay: '150ms' }} />
                    <div className="w-1.5 h-1.5 rounded-full bg-[#6B9E8A] animate-bounce" style={{ animationDelay: '300ms' }} />
                  </div>
                </div>
              </div>
            )}
            <div ref={messagesEndRef} />
          </div>
        </div>

        {/* Input */}
        <div className="border-t border-[#1A3A4A]/10 dark:border-white/10 bg-white/80 dark:bg-[#141C22]/90 px-4 py-3">
          {listening && (
            <div className="flex items-center gap-2 mb-2 px-1">
              <div className="flex gap-0.5">
                {[0,1,2,3,4].map(i => (
                  <div key={i} className="w-1 h-3 rounded-full bg-[#C67A5C] animate-pulse" style={{ animationDelay: `${i*100}ms` }} />
                ))}
              </div>
              <span className="text-xs text-[#C67A5C] font-medium">Listening...</span>
            </div>
          )}
          <div className="flex items-end gap-2">
            {voiceSupported && (
              <button
                data-testid="coach-panel-voice-btn"
                onClick={toggleListening}
                className={`h-10 w-10 rounded-xl flex items-center justify-center transition-all ${
                  listening ? 'bg-[#C67A5C] text-white scale-110 animate-pulse' : 'bg-[#1A2530] text-[#A1A1AA] hover:text-[#F0EDE8]'
                }`}
                style={{ touchAction: 'manipulation' }}
              >
                {listening ? <MicOff className="w-4 h-4" /> : <Mic className="w-4 h-4" />}
              </button>
            )}
            <input
              ref={inputRef}
              data-testid="coach-panel-input"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder="Quick question..."
              className="flex-1 bg-[#F5F2ED] dark:bg-[#1A2530] border border-[#1A3A4A]/10 dark:border-white/10 rounded-xl px-3.5 py-2.5 text-sm text-[#1A3A4A] dark:text-[#F0EDE8] placeholder-[#7A7A72] focus:outline-none focus:ring-2 focus:ring-[#6B9E8A]/30"
            />
            <Button
              data-testid="coach-panel-send"
              onClick={sendMessage}
              disabled={!input.trim() || sending}
              className="h-10 w-10 rounded-xl bg-[#6B9E8A] hover:bg-[#5A8D79] text-white p-0 disabled:opacity-40"
            >
              {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CoachPanel;
